/**
 * Analytics service for tracking user interactions and sending data to Google Tag Manager
 */

// Define types for analytics data
interface AnalyticsEvent {
  event?: string;
  category?: string;
  action?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

interface UserData {
  userId?: string;
  email?: string;
  name?: string;
  role?: string;
  [key: string]: any;
}

// Extend Window interface for GTM
declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

class Analytics {
  private static instance: Analytics | null = null;
  private isInitialized = false;
  private userData: UserData | null = null;

  // Singleton pattern
  static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  /**
   * Initialize the data layer if it doesn't exist
   */
  private initializeDataLayer(): void {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    this.isInitialized = true;
  }

  /**
   * Set user data for the current session
   */
  static setUser = (userData?: UserData): void => {
    const instance = Analytics.getInstance();
    instance.setUserData(userData);
  };

  /**
   * Send analytics event data
   */
  static send = (eventData: AnalyticsEvent): void => {
    const instance = Analytics.getInstance();
    instance.sendEvent(eventData);
  };

  /**
   * Set user data for tracking
   */
  private setUserData(userData?: UserData): void {
    if (!this.isInitialized) {
      this.initializeDataLayer();
    }

    this.userData = userData || null;

    if (userData && window.dataLayer) {
      window.dataLayer.push({
        event: "user_data_set",
        user_id: userData.userId,
        user_email: userData.email,
        user_name: userData.name,
        user_role: userData.role,
        ...userData,
      });
    }
  }

  /**
   * Send event data to analytics
   */
  private sendEvent(eventData: AnalyticsEvent): void {
    if (!this.isInitialized) {
      this.initializeDataLayer();
    }

    if (!window.dataLayer) {
      console.warn("Analytics: dataLayer not available");
      return;
    }

    try {
      // Merge user data with event data
      const enrichedEventData = {
        ...eventData,
        ...(this.userData && { user_data: this.userData }),
        timestamp: new Date().toISOString(),
      };

      window.dataLayer.push(enrichedEventData);

      // Also send to gtag if available
      if (window.gtag && eventData.event) {
        window.gtag("event", eventData.event, {
          event_category: eventData.category,
          event_label: eventData.label,
          value: eventData.value,
          ...eventData,
        });
      }
    } catch (_error) {
      console.error("Analytics: Failed to send event");
    }
  }

  /**
   * Clear user data
   */
  static clearUser = (): void => {
    const instance = Analytics.getInstance();
    instance.userData = null;
  };

  /**
   * Track page view
   */
  static trackPageView = (pagePath: string, pageTitle?: string): void => {
    Analytics.send({
      event: "page_view",
      page_path: pagePath,
      page_title: pageTitle || document.title,
    });
  };

  /**
   * Track custom event with common event types
   */
  static trackEvent = (
    eventName: string,
    category?: string,
    action?: string,
    label?: string,
    value?: number,
  ): void => {
    Analytics.send({
      event: eventName,
      category,
      action,
      label,
      value,
    });
  };
}

export default Analytics;

// Export types for external usage
export type { AnalyticsEvent, UserData };
